/*
 * These utils are an extra abstraction underneath the existing general util file. Tokenizer should
 * never be importing and using this file directly - any usage of it should be via the general util
 * file instead.
 */

import _ from 'lodash';
import { BrowserName, browserName } from '/@utils/browser.util';
import { isLeft, isRight } from '/@utils/event-utils';

function getClosestElementNode(node: Element, parentNode: Element): Element {
    if (node.previousElementSibling) {
        return node.previousElementSibling;
    }
    return parentNode;
}

// TODO(Rifdhan) return type can be null
export function getNodeAtCaret(container: Element): Element {
    const selection = document.getSelection();
    let anchorNode: Node = (selection as any).baseNode || selection.anchorNode;
    if (!anchorNode) {
        // TODO(Rifdhan) is this an error case?
        return null as any;
    }

    // If the selection anchor node is not even in the sage bar, then use the last token element
    // inside the sage bar instead to ensure sanity
    if (anchorNode === container || !container.contains(anchorNode)) {
        anchorNode = container.lastChild || container;
    }

    if (anchorNode.nodeName === '#text') {
        if (anchorNode.parentNode === container) {
            anchorNode = getClosestElementNode(
                anchorNode as Element,
                anchorNode.parentNode as Element,
            );
        } else {
            anchorNode = anchorNode.parentNode as Node;
        }
    }

    return anchorNode as Element;
}

/*
 * Whether the caret is at the left-most edge of any token (doesn't matter which token). Considered
 * to be true if the caret is in an empty separator.
 */
const isCaretAtStartOfToken = () => {
    const range = document.getSelection().getRangeAt(0);
    return range.endOffset === 0;
};

/*
 * Whether the caret is at the right-most edge of any token (doesn't matter which token).
 * Considered to be true if the caret is in an empty separator.
 */
const isCaretAtEndOfToken = () => {
    const range = document.getSelection().getRangeAt(0);
    const rangeEndElementTextLength = range.endContainer.textContent.length;
    return range.endOffset === rangeEndElementTextLength;
};

/*
 * Tokens which contain text will have a #text node inside the token element. If called with that
 * #text node as an argument, we want to return the parent token element.
 */
const getTokenOrSeparatorNode = (element: HTMLElement | Node) => {
    return element.nodeName === '#text'
        ? (element.parentNode as HTMLElement)
        : element;
};

/*
 * Move the caret into the start of the next node (token or separator)
 */
export const moveCaretIntoNextNodeStart = () => {
    const selection = document.getSelection();
    const range = selection.getRangeAt(0);

    const rangeEndElement = getTokenOrSeparatorNode(range.endContainer);
    const nextElement =
        rangeEndElement.nextSibling.firstChild || rangeEndElement.nextSibling;

    const newRange = document.createRange();
    newRange.setStart(nextElement, 0);
    newRange.setEnd(nextElement, 0);

    selection.removeAllRanges();
    selection.addRange(newRange);
};

/*
 * Move the caret into the end of the previous node (token or separator)
 */
export const moveCaretIntoPreviousNodeEnd = () => {
    const selection = document.getSelection();
    const range = selection.getRangeAt(0);

    const rangeEndElement = getTokenOrSeparatorNode(range.endContainer);
    // We want to select the #text node if it exists, which is the child element
    const previousElement =
        rangeEndElement.previousSibling.firstChild ||
        rangeEndElement.previousSibling;

    const newRange = document.createRange();
    newRange.setStart(previousElement, previousElement.textContent.length);
    newRange.setEnd(previousElement, previousElement.textContent.length);

    selection.removeAllRanges();
    selection.addRange(newRange);
};

/*
 * When pressing some types of keys, we require special handling of some scenarios for some
 * browsers since their default behavior is not the desired behavior.
 */
export function getKeydownSpecialCaseHandler(): (
    container: Element,
    evt: KeyboardEvent,
) => void {
    const keydownSpecialCaseHandlerByBrowser = {
        default: _.noop,
        [BrowserName.Firefox]: (container: Element, evt: KeyboardEvent) => {
            const caretNode = getNodeAtCaret(container);

            if (
                isLeft(evt) &&
                caretNode !== container.firstChild &&
                isCaretAtStartOfToken()
            ) {
                moveCaretIntoPreviousNodeEnd();
                evt.preventDefault();
            } else if (
                isRight(evt) &&
                caretNode !== container.lastChild &&
                isCaretAtEndOfToken()
            ) {
                moveCaretIntoNextNodeStart();
                evt.preventDefault();
            }
        },
    };
    return (
        keydownSpecialCaseHandlerByBrowser[
            browserName as keyof typeof keydownSpecialCaseHandlerByBrowser
        ] || keydownSpecialCaseHandlerByBrowser.default
    );
}
